import { Link } from 'react-router-dom'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import { PageHeader, StatCard } from '@/components/ui'
import {
  IconUsers, IconPackage, IconFileText, IconKanban, IconUser,
  IconPlus, IconSettings, IconBriefcase,
} from '@/components/icons/Icons'
import type { Cliente, Servicio, Cotizacion, PerfilEquipo, Proyecto } from '@/lib/types'

/*
  Inicio — el recuento de lo que hay en el ERP y los atajos de todos los días.

  Pantalla piloto del design system (prd/core/05-Design-System.md): encabeza con
  breadcrumb y no con título, compone con primitivas y no define ni un color.

  Los ocho íconos vivían acá dentro, redibujados a mano. Salen del catálogo
  (`Icons.tsx`) por la misma razón por la que los colores salen de tokens: un
  ícono copiado es un ícono que deja de parecerse al resto en cuanto alguno de
  los dos se toca.
*/

type Tono = 'success' | 'warning' | 'danger' | undefined

interface Tarjeta {
  label: string
  value: number
  tone: Tono
  to: string
  icon: React.ReactNode
}

export default function Dashboard() {
  const { data: clientes, isLoading: l1 } = useCachedQuery<Cliente>('clientes')
  const { data: servicios, isLoading: l2 } = useCachedQuery<Servicio>('servicios')
  const { data: cotizaciones, isLoading: l3 } = useCachedQuery<Cotizacion>('cotizaciones')
  const { data: perfiles, isLoading: l4 } = useCachedQuery<PerfilEquipo>('perfiles_equipo')
  const { data: proyectos, isLoading: l5 } = useCachedQuery<Proyecto>('proyectos')

  const loading = l1 || l2 || l3 || l4 || l5

  const tarjetas: Tarjeta[] = [
    { label: 'Clientes', value: clientes?.length ?? 0, tone: undefined, to: '/clientes', icon: <IconUsers size={16} /> },
    { label: 'Servicios', value: servicios?.length ?? 0, tone: 'success', to: '/servicios', icon: <IconPackage size={16} /> },
    { label: 'Cotizaciones', value: cotizaciones?.length ?? 0, tone: 'warning', to: '/cotizaciones', icon: <IconFileText size={16} /> },
    { label: 'Proyectos', value: proyectos?.length ?? 0, tone: undefined, to: '/proyectos', icon: <IconKanban size={16} /> },
    { label: 'Perfiles', value: perfiles?.length ?? 0, tone: undefined, to: '/perfiles', icon: <IconUser size={16} /> },
  ]

  const atajos = [
    { to: '/cotizaciones/nueva', label: 'Nueva Cotización', icon: <IconFileText size={15} /> },
    { to: '/equipo/nueva', label: 'Equipo Dedicado', icon: <IconUsers size={15} /> },
    { to: '/proyectos', label: 'Proyectos', icon: <IconKanban size={15} /> },
    { to: '/clientes', label: 'Gestionar Clientes', icon: <IconBriefcase size={15} /> },
    { to: '/configuracion', label: 'Configuración', icon: <IconSettings size={15} /> },
  ]

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'Inicio' }]}
        actions={
          <Link to="/cotizaciones/nueva" className="btn btn-primary">
            <IconPlus size={16} /> Nueva Cotización
          </Link>
        }
      />

      <div className="stats-grid">
        {tarjetas.map((c) => (
          // La tarjeta entera es el enlace: el número sin el nombre no se puede
          // pulsar con confianza, y dos blancos (el número y la flecha) en una
          // tarjeta de cinco palabras sobran.
          <Link key={c.label} to={c.to} className="stat-link">
            <StatCard
              label={<><span className="stat-label__icon">{c.icon}</span>{c.label}</>}
              value={loading ? '—' : c.value}
              tone={c.tone}
            />
          </Link>
        ))}
      </div>

      <div className="card">
        <div className="card-header"><h2>Acciones Rápidas</h2></div>
        <div className="quick-actions">
          {atajos.map((a) => (
            <Link key={a.to} to={a.to} className="btn btn-secondary">
              {a.icon} {a.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
